import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, getProfile, isUserBanned } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [banned,  setBanned]  = useState(false);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (authUser) => {
    if (!authUser) { setProfile(null); setBanned(false); return; }
    const p = await getProfile(authUser.id);
    setProfile(p);
    const b = await isUserBanned(authUser.id);
    setBanned(!!b);
    // If banned, force sign out so they can't keep using the site
    if (b) {
      await supabase.auth.signOut();
      setUser(null); setProfile(null); setBanned(true);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      loadProfile(session?.user ?? null).then(() => setLoading(false));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      loadProfile(session?.user ?? null);
      if (event === 'SIGNED_IN' && window.location.hash.includes('access_token')) {
        window.history.replaceState({}, document.title, '/');
      }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null); setProfile(null); setBanned(false);
    window.history.replaceState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const refreshProfile = () => loadProfile(user);

  return (
    <AuthContext.Provider value={{ user, profile, banned, loading, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
